import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

// Add caching for better performance
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedData = (key: string) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  cache.set(key, { data, timestamp: Date.now() });
};

export const useDonorData = () => {
  const { profile } = useAuth();
  const [donor, setDonor] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDonor = useCallback(async () => {
    if (!profile || profile.role !== 'donor') {
      setLoading(false);
      return;
    }

    const cacheKey = `donor-${profile.id}`;
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      setDonor(cachedData);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await Promise.race([
        supabase
          .from('donors')
          .select('*')
          .eq('profile_id', profile.id)
          .maybeSingle(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 5000)
        )
      ]) as any;
      
      if (error) throw error;
      setDonor(data);
      setCachedData(cacheKey, data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    fetchDonor();
  }, [fetchDonor]);

  const updateDonor = async (updates: any) => {
    try {
      if (donor) {
        const { data, error } = await supabase
          .from('donors')
          .update(updates)
          .eq('id', donor.id)
          .select()
          .maybeSingle();
        
        if (error) throw error;
        setDonor(data);
        setCachedData(`donor-${profile?.id}`, data);
        return { data, error: null };
      } else {
        const { data, error } = await supabase
          .from('donors')
          .insert({ ...updates, profile_id: profile?.id })
          .select()
          .maybeSingle();
        
        if (error) throw error;
        setDonor(data);
        setCachedData(`donor-${profile?.id}`, data);
        return { data, error: null };
      }
    } catch (err: any) {
      return { data: null, error: err.message };
    }
  };

  return { donor, loading, error, updateDonor, refetch: fetchDonor };
};

export const useRecipientData = () => {
  const { profile } = useAuth();
  const [recipient, setRecipient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRecipient = useCallback(async () => {
    if (!profile || profile.role !== 'recipient') {
      setLoading(false);
      return;
    }

    const cacheKey = `recipient-${profile.id}`;
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      setRecipient(cachedData);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await Promise.race([
        supabase
          .from('recipients')
          .select('*')
          .eq('profile_id', profile.id)
          .maybeSingle(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 5000)
        )
      ]) as any;
      
      if (error) throw error;
      setRecipient(data);
      setCachedData(cacheKey, data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    fetchRecipient();
  }, [fetchRecipient]);

  const updateRecipient = async (updates: any) => {
    try {
      if (recipient) {
        const { data, error } = await supabase
          .from('recipients')
          .update(updates)
          .eq('id', recipient.id)
          .select()
          .maybeSingle();
        
        if (error) throw error;
        setRecipient(data);
        setCachedData(`recipient-${profile?.id}`, data);
        return { data, error: null };
      } else {
        const { data, error } = await supabase
          .from('recipients')
          .insert({ ...updates, profile_id: profile?.id })
          .select()
          .maybeSingle();
        
        if (error) throw error;
        setRecipient(data);
        setCachedData(`recipient-${profile?.id}`, data);
        return { data, error: null };
      }
    } catch (err: any) {
      return { data: null, error: err.message };
    }
  };

  return { recipient, loading, error, updateRecipient, refetch: fetchRecipient };
};

export const useHospitalData = () => {
  const { profile } = useAuth();
  const [hospital, setHospital] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHospital = useCallback(async () => {
    if (!profile || profile.role !== 'hospital') {
      setLoading(false);
      return;
    }

    const cacheKey = `hospital-${profile.id}`;
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      setHospital(cachedData);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await Promise.race([
        supabase
          .from('hospitals')
          .select('*')
          .eq('profile_id', profile.id)
          .maybeSingle(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 5000)
        )
      ]) as any;
      
      if (error) throw error;
      setHospital(data);
      setCachedData(cacheKey, data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    fetchHospital();
  }, [fetchHospital]);

  const updateHospital = async (updates: any) => {
    try {
      if (hospital) {
        const { data, error } = await supabase
          .from('hospitals')
          .update(updates)
          .eq('id', hospital.id)
          .select()
          .maybeSingle();
        
        if (error) throw error;
        setHospital(data);
        setCachedData(`hospital-${profile?.id}`, data);
        return { data, error: null };
      } else {
        const { data, error } = await supabase
          .from('hospitals')
          .insert({ ...updates, profile_id: profile?.id })
          .select()
          .maybeSingle();
        
        if (error) throw error;
        setHospital(data);
        setCachedData(`hospital-${profile?.id}`, data);
        return { data, error: null };
      }
    } catch (err: any) {
      return { data: null, error: err.message };
    }
  };

  return { hospital, loading, error, updateHospital, refetch: fetchHospital };
};

export const useMedicalRecords = () => {
  const { profile } = useAuth();
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRecords = useCallback(async () => {
    if (!profile) {
      setLoading(false);
      return;
    }

    const cacheKey = `medical-records-${profile.id}`;
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      setRecords(cachedData);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await Promise.race([
        supabase
          .from('medical_records')
          .select('*')
          .eq('profile_id', profile.id)
          .order('test_date', { ascending: false })
          .limit(10), // Limit initial load
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 5000)
        )
      ]) as any;
      
      if (error) throw error;
      setRecords(data || []);
      setCachedData(cacheKey, data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const addRecord = async (recordData: any) => {
    try {
      const { data, error } = await supabase
        .from('medical_records')
        .insert({ ...recordData, profile_id: profile?.id })
        .select()
        .maybeSingle();
      
      if (error) throw error;
      setRecords(prev => [data, ...prev]);
      // Clear cache to force refresh
      cache.delete(`medical-records-${profile?.id}`);
      return { data, error: null };
    } catch (err: any) {
      return { data: null, error: err.message };
    }
  };

  return { records, loading, error, addRecord, refetch: fetchRecords };
};

export const useAppointments = () => {
  const { profile } = useAuth();
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAppointments = useCallback(async () => {
    if (!profile) {
      setLoading(false);
      return;
    }

    const cacheKey = `appointments-${profile.id}`;
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      setAppointments(cachedData);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await Promise.race([
        supabase
          .from('appointments')
          .select('*')
          .eq('profile_id', profile.id)
          .order('appointment_date', { ascending: true })
          .limit(20), // Limit initial load
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 5000)
        )
      ]) as any;
      
      if (error) throw error;
      setAppointments(data || []);
      setCachedData(cacheKey, data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const addAppointment = async (appointmentData: any) => {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .insert({ ...appointmentData, profile_id: profile?.id })
        .select()
        .maybeSingle();
      
      if (error) throw error;
      setAppointments(prev => [...prev, data].sort((a, b) => 
        new Date(a.appointment_date).getTime() - new Date(b.appointment_date).getTime()
      ));
      // Clear cache to force refresh
      cache.delete(`appointments-${profile?.id}`);
      return { data, error: null };
    } catch (err: any) {
      return { data: null, error: err.message };
    }
  };

  return { appointments, loading, error, addAppointment, refetch: fetchAppointments };
};

export const useNotifications = () => {
  const { profile } = useAuth();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (!profile) {
      setLoading(false);
      return;
    }

    const cacheKey = `notifications-${profile.id}`;
    const cachedData = getCachedData(cacheKey);
    
    if (cachedData) {
      setNotifications(cachedData);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await Promise.race([
        supabase
          .from('notifications')
          .select('*')
          .eq('profile_id', profile.id)
          .order('created_at', { ascending: false })
          .limit(50), // Limit initial load
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 5000)
        )
      ]) as any;
      
      if (error) throw error;
      setNotifications(data || []);
      setCachedData(cacheKey, data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', id)
        .select()
        .maybeSingle();
      
      if (error) throw error;
      setNotifications(prev => 
        prev.map(notif => notif.id === id ? { ...notif, read: true } : notif)
      );
      // Update cache
      const cacheKey = `notifications-${profile?.id}`;
      const updatedNotifications = notifications.map(notif => 
        notif.id === id ? { ...notif, read: true } : notif
      );
      setCachedData(cacheKey, updatedNotifications);
      return { data, error: null };
    } catch (err: any) {
      return { data: null, error: err.message };
    }
  };

  return { notifications, loading, error, markAsRead, refetch: fetchNotifications };
};